import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import logo from "../assets/Provinyx-logo.png";
import acespireLogo from "../assets/Acespire-solutions.png";
import ContactModal from "../components/Contactmodal"; // adjust path if needed

// ── Reusable modal shell ──────────────────────────────────────────────────────
function InfoModal({ title, children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden shadow-2xl"
        style={{ animation: "modalIn 0.22s cubic-bezier(0.34,1.56,0.64,1) both" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors">
            <X size={16} className="text-gray-600" />
          </button>
        </div>
        <div className="px-7 py-6 overflow-y-auto text-gray-600 text-sm leading-relaxed space-y-4">
          {children}
        </div>
      </div>
      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.92) translateY(14px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

function AboutUsModal({ onClose }) {
  return (
    <InfoModal title="About Us" onClose={onClose}>
      <p>A flagship innovation of Acespire Consulting, Provinyx is transforming product authentication and supply chain transparency through its powerful Digital Product Passport (DPP) solutions.</p>
      <p>Our cutting-edge platform creates a seamless connection between physical products and their digital identities, assigning each product a unique, scannable identifier that links to a secure, cloud-based digital record.</p>
      <p>This record contains verified information about the product's origin, manufacturing process, lifecycle data, and sustainability credentials, providing real-time access to accurate and tamper-proof product intelligence throughout the supply chain.</p>
    </InfoModal>
  );
}

function OurVisionModal({ onClose }) {
  return (
    <InfoModal title="Our Vision" onClose={onClose}>
      <p>In today's marketplace, where counterfeiting poses a significant threat to brand reputation and consumer trust, Provinyx offers a comprehensive anti-counterfeiting solution that effectively prevents product fraud.</p>
      <p>By registering each product's identity on an immutable ledger, our technology enables instant verification, end-to-end traceability of a particular product, real-time monitoring of authentication attempts, and early detection of counterfeit activity.</p>
      <p>This robust solution empowers brands, manufacturers, and consumers to ensure the authenticity of products across various industries, including luxury goods, electronics, and apparel. Moreover, our technology helps companies meet emerging EU Digital Product Passport compliance requirements, ultimately fostering lasting consumer confidence through verified authenticity and protecting brand integrity.</p>
    </InfoModal>
  );
}

function PrivacyModal({ onClose }) {
  return (
    <InfoModal title="Privacy Policy" onClose={onClose}>
      <h3 className="font-semibold text-gray-800 text-base">1. Information We Collect</h3>
      <p>We collect information you provide directly to us, such as when you request a demo, contact us, or use our platform. This may include your name, email address, phone number, and country of residence.</p>
      <h3 className="font-semibold text-gray-800 text-base">2. How We Use Your Information</h3>
      <p>Provinyx uses collected information to provide and improve our Digital Product Passport services, respond to your inquiries, send product updates and relevant communications, and ensure EU regulatory compliance.</p>
      <h3 className="font-semibold text-gray-800 text-base">3. Data Storage & Security</h3>
      <p>All data is stored on secure, encrypted cloud infrastructure. We implement enterprise-grade security measures including blockchain-backed records to ensure tamper-proof data integrity.</p>
      <h3 className="font-semibold text-gray-800 text-base">4. Data Sharing</h3>
      <p>We do not sell your personal data. We may share data with trusted service providers who assist in operating our platform, subject to strict confidentiality agreements.</p>
      <h3 className="font-semibold text-gray-800 text-base">5. Your Rights (GDPR)</h3>
      <p>Under the EU General Data Protection Regulation, you have the right to access, correct, delete, or restrict processing of your personal data. Contact us at info@acespireconsulting.com to exercise these rights.</p>
      <h3 className="font-semibold text-gray-800 text-base">6. Cookies</h3>
      <p>We use cookies to enhance your browsing experience. You can manage cookie preferences through our Cookie Policy at any time.</p>
      <h3 className="font-semibold text-gray-800 text-base">7. Contact</h3>
      <p>For privacy-related queries, contact us at <span className="text-blue-600">info@acespireconsulting.com</span>.</p>
    </InfoModal>
  );
}

function TermsModal({ onClose }) {
  return (
    <InfoModal title="Terms of Service" onClose={onClose}>
      <h3 className="font-semibold text-gray-800 text-base">1. Acceptance of Terms</h3>
      <p>By accessing or using the Provinyx platform, you agree to be bound by these Terms of Service. If you do not agree, please discontinue use immediately.</p>
      <h3 className="font-semibold text-gray-800 text-base">2. Description of Service</h3>
      <p>Provinyx provides Digital Product Passport (DPP) solutions enabling product authentication, supply chain transparency, and EU regulatory compliance for brands and enterprises.</p>
      <h3 className="font-semibold text-gray-800 text-base">3. User Responsibilities</h3>
      <p>You agree to provide accurate information, maintain the security of your account credentials, and use the platform only for lawful purposes consistent with EU DPP regulations.</p>
      <h3 className="font-semibold text-gray-800 text-base">4. Intellectual Property</h3>
      <p>All content, technology, and trademarks on the Provinyx platform are the exclusive property of Provinyx / AceSpire Consulting. Unauthorised reproduction is prohibited.</p>
      <h3 className="font-semibold text-gray-800 text-base">5. Limitation of Liability</h3>
      <p>Provinyx shall not be liable for indirect, incidental, or consequential damages arising from use of the platform. Our liability is limited to the fees paid for the service in the preceding 12 months.</p>
      <h3 className="font-semibold text-gray-800 text-base">6. Governing Law</h3>
      <p>These terms are governed by the laws of the European Union and applicable member state regulations, including GDPR and EU DPP compliance frameworks.</p>
      <h3 className="font-semibold text-gray-800 text-base">7. Contact</h3>
      <p>Questions about these terms? Reach us at <span className="text-blue-600">info@acespireconsulting.com</span>.</p>
    </InfoModal>
  );
}

function CookiesModal({ onClose }) {
  return (
    <InfoModal title="Cookie Policy" onClose={onClose}>
      <h3 className="font-semibold text-gray-800 text-base">1. What Are Cookies?</h3>
      <p>Cookies are small text files placed on your device when you visit the Provinyx platform. They help us recognise your device, remember your preferences, and improve your overall experience.</p>
      <h3 className="font-semibold text-gray-800 text-base">2. How We Use Cookies</h3>
      <p>Provinyx uses cookies to ensure the platform functions correctly, keep you securely logged in, understand how visitors navigate our site, and improve the performance and relevance of our Digital Product Passport services.</p>
      <h3 className="font-semibold text-gray-800 text-base">3. Types of Cookies We Use</h3>
      <div className="space-y-3">
        {[
          { label: "Essential Cookies",  desc: "Necessary for the platform to operate. They enable core features such as authentication, session management, and security. These cookies cannot be disabled." },
          { label: "Analytics Cookies",  desc: "Help us understand how users interact with our platform — which pages are visited most, where users come from, and how they navigate. All data is aggregated and anonymised." },
          { label: "Functional Cookies", desc: "Remember your preferences such as language settings and display options to provide a more personalised experience on return visits." },
          { label: "Marketing Cookies",  desc: "Used to deliver relevant content and measure the effectiveness of our outreach campaigns. We do not use these cookies to sell your data to third parties." },
        ].map(({ label, desc }) => (
          <div key={label} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
            <p className="font-semibold text-gray-800 text-sm mb-1">{label}</p>
            <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
      <h3 className="font-semibold text-gray-800 text-base">4. Third-Party Cookies</h3>
      <p>Some features of our platform may use third-party services that set their own cookies. These are governed by their respective privacy policies and are used only to support the delivery of our services.</p>
      <h3 className="font-semibold text-gray-800 text-base">5. Managing Cookies</h3>
      <p>You can control and delete cookies through your browser settings at any time. Please note that disabling certain cookies may affect the functionality of the Provinyx platform.</p>
      <h3 className="font-semibold text-gray-800 text-base">6. GDPR Compliance</h3>
      <p>In accordance with the EU General Data Protection Regulation (GDPR), we only set non-essential cookies with your explicit consent. You may withdraw consent at any time by adjusting your browser settings or contacting us directly.</p>
      <h3 className="font-semibold text-gray-800 text-base">7. Contact Us</h3>
      <p>If you have any questions about our Cookie Policy, please contact us at <span className="text-blue-600">info@acespireconsulting.com</span>.</p>
    </InfoModal>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const location = useLocation();
  const navigate  = useNavigate();
  const [modal,        setModal]        = useState(null); // 'about'|'vision'|'privacy'|'terms'|'cookies'
  const [contactOpen,  setContactOpen]  = useState(false);

  const handleScroll = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePathNav = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const companyLinks = [
    { name: "Home",          path: "/" },
    { name: "Authenticator", path: "/authenticator" },
    { name: "Blogs",         path: "/blog" },
    { name: "About",      path: "/about" },
    { name: "Our Vision",    modal: "vision" },
  ];

  const productLinks = [
    { name: "Features",     id: "features" },
    { name: "How It Works", id: "how-it-works" },
    { name: "Benefits",     id: "benefits" },
  ];

  const connectLinks = [
    { name: "Contact", action: () => setContactOpen(true) },
  ];

  const renderLink = (item) => (
    <li key={item.name}>
      <button
        onClick={() =>
          item.action ? item.action() :
          item.modal  ? setModal(item.modal) :
          item.path   ? handlePathNav(item.path) :
          handleScroll(item.id)
        }
        className="text-gray-400 hover:text-blue-500 transition-colors bg-transparent border-none p-0 cursor-pointer text-sm"
      >
        {item.name}
      </button>
    </li>
  );

  return (
    <>
      <footer className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* ── Main grid: brand left, 3 columns right ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">

            {/* Brand — takes 1 col on lg */}
            <div className="lg:col-span-2">
              <button onClick={() => handlePathNav("/")} className="flex items-center focus:outline-none mb-4">
                <img src={logo} loading="lazy" alt="Provinyx" className="h-8 w-auto object-contain" />
              </button>
              <p className="text-gray-400 leading-relaxed text-sm">
                Enterprise-grade Digital Product Passport platform enabling
                transparency, traceability, and sustainability compliance.
              </p>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wide">Company</h3>
              <ul className="space-y-3">{companyLinks.map(renderLink)}</ul>
            </div>

            {/* Products */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wide">Products</h3>
              <ul className="space-y-3">{productLinks.map(renderLink)}</ul>
            </div>

            {/* Connect */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wide">Connect</h3>
              <ul className="space-y-3">{connectLinks.map(renderLink)}</ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">

              {/* Copyright */}
              <p className="text-gray-400">© 2026 Provinyx. All rights reserved.</p>

              {/* AceSpire branding */}
              <a
                href="https://acespiresolutions.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 group"
              >
                <span className="text-gray-500 text-xs">A product of</span>
                <img
                  src={acespireLogo}
                  loading="lazy"
                  alt="Acespire Solutions"
                  className="h-12 w-auto object-contain" //opacity-70 group-hover:opacity-100 transition-opacity duration-200...for hover effect
                />
              </a>

              {/* Legal links */}
              <div className="flex items-center gap-6">
                <button onClick={() => setModal("privacy")} className="text-gray-400 hover:text-blue-500 transition-colors bg-transparent border-none cursor-pointer text-sm">Privacy Policy</button>
                <button onClick={() => setModal("terms")}   className="text-gray-400 hover:text-blue-500 transition-colors bg-transparent border-none cursor-pointer text-sm">Terms of Service</button>
                <button onClick={() => setModal("cookies")} className="text-gray-400 hover:text-blue-500 transition-colors bg-transparent border-none cursor-pointer text-sm">Cookie Policy</button>
              </div>
            </div>
          </div>

        </div>
      </footer>

      {/* Modals */}
      {modal === "about"   && <AboutUsModal   onClose={() => setModal(null)} />}
      {modal === "vision"  && <OurVisionModal onClose={() => setModal(null)} />}
      {modal === "privacy" && <PrivacyModal   onClose={() => setModal(null)} />}
      {modal === "terms"   && <TermsModal     onClose={() => setModal(null)} />}
      {modal === "cookies" && <CookiesModal   onClose={() => setModal(null)} />}
      {contactOpen         && <ContactModal   onClose={() => setContactOpen(false)} />}
    </>
  );
}

export default Footer;